import React from 'react';
import { NotebookPen, Lightbulb } from 'lucide-react';
import SeverityFace from '../SeverityFace';
import { DAY_PERIODS, SEVERITY_SCALE } from '../../../../constants/journey';
import {
	formatTime,
	minutesSinceDayStart,
} from '../../../../utils/journeyDates';
import styles from './DayTimeline.module.css';

// The timeline runs 6am to 6am, so entries are ordered and spaced by minutes
// since 6am. Spacing is proportional rather than absolute: a fixed-height track
// would overlap the three entries Sarah often makes within the same half hour,
// and a card is not readable stacked on another.
const PIXELS_PER_MINUTE = 0.35;
const MAX_GAP = 96;

const PERIOD_LABELS = DAY_PERIODS.reduce((map, period) => {
	map[period.key] = period.label;
	return map;
}, {});

// The day view is the whole day across every goal, so it ignores the goal
// filter — entries from different goals sit on the same timeline.
const DayTimeline = ({ entries = [], period }) => {
	if (entries.length === 0) {
		return (
			<p className={styles.empty}>
				<span>Nothing tracked on {period.label}.</span>
				<span>
					Ratings you record on the Tracking page appear here, in the order you
					made them.
				</span>
			</p>
		);
	}

	const ordered = [...entries]
		.map((entry) => ({
			...entry,
			minute: minutesSinceDayStart(entry.start_time),
		}))
		.sort((a, b) => a.minute - b.minute);

	let previousMinute = null;
	let previousPeriod = null;

	return (
		<ol className={styles.timeline}>
			{ordered.map((entry) => {
				const gap =
					previousMinute === null
						? 0
						: Math.min((entry.minute - previousMinute) * PIXELS_PER_MINUTE, MAX_GAP);
				const startsNewPeriod = entry.period !== previousPeriod;
				previousMinute = entry.minute;
				previousPeriod = entry.period;

				return (
					<li
						key={entry.rating_log_id}
						className={styles.item}
						style={{ marginTop: `${gap}px` }}
					>
						{startsNewPeriod && (
							<span className={styles.periodDivider}>
								{PERIOD_LABELS[entry.period]}
							</span>
						)}

						<div className={styles.row}>
							<time className={styles.time}>
								{formatTime(entry.start_time)}
								{entry.end_time && (
									<span className={styles.endTime}>
										– {formatTime(entry.end_time)}
									</span>
								)}
							</time>

							<span className={styles.rail} aria-hidden='true' />

							<div className={styles.card}>
								<div className={styles.cardHeader}>
									<SeverityFace
										rating={entry.rating}
										size={32}
										className={styles.face}
									/>
									<div className={styles.cardTitles}>
										<span className={styles.termName}>{entry.term_name}</span>
										<span className={styles.severityLabel}>
											{SEVERITY_SCALE[entry.rating]?.label}
										</span>
									</div>
								</div>

								{/* Written content sits beside the rating rather than under
								    it, so a run of entries reads as two columns: what was
								    rated on the left, what was said about it on the right.
								    Omitted entirely when there is nothing to say, so the card
								    does not carry an empty second column. */}
								{(entry.journal?.trim() ||
									entry.tip?.care_tip?.message?.trim()) && (
									<div className={styles.cardBody}>
										{/* Blank-but-present text is treated as no journal: an
										    entry saved with only whitespace must not draw an
										    empty block. */}
										{entry.journal?.trim() && (
											<p className={styles.journal}>
												<NotebookPen className={styles.journalIcon} />
												{entry.journal.trim()}
											</p>
										)}

										{/* The tip this submission produced. The server pairs
										    them; tips with no submission behind them come back
										    separately as standalone_tips and are not shown. */}
										{entry.tip?.care_tip?.message?.trim() && (
											<p className={styles.tip}>
												<Lightbulb className={styles.tipIcon} />
												{entry.tip.care_tip.message.trim()}
											</p>
										)}
									</div>
								)}
							</div>
						</div>
					</li>
				);
			})}
		</ol>
	);
};

export default DayTimeline;
